import React, { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
} from "../Statistics/components/statsSignedPnlBarUtils";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnBarChartContainerClassName,
} from "../Statistics/components/statisticsShadcnCardShell";
import type {
  StrategyRdApplyOverview,
  StrategyRdPeriodPnlBucket,
} from "../../../types/strategyRd";
import { formatStrategyRdMoney } from "../../../utils/strategyRd";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import { strategyRdCardTitleClassName, strategyRdSectionCardClassName } from "./strategyRdLabUi";

const pnlChartConfig = {
  pnl: { label: "PnL", color: PNL_POSITIVE_COLOR },
} satisfies ChartConfig;

function PeriodPnlChart({
  daily,
  weekly,
  currency,
}: {
  daily: StrategyRdPeriodPnlBucket[];
  weekly: StrategyRdPeriodPnlBucket[];
  currency: string | null;
}): JSX.Element | null {
  const [period, setPeriod] = useState<"daily" | "weekly" | "monthly">(
    daily.length > 0 ? "daily" : "weekly"
  );
  // Months: the daily buckets summed per calendar month.
  const monthly = useMemo((): StrategyRdPeriodPnlBucket[] => {
    const by = new Map<string, number>();
    for (const b of daily) {
      const key = (b.date || "").slice(0, 7);
      if (key.length === 7) by.set(key, (by.get(key) ?? 0) + Number(b.pnl ?? 0));
    }
    return Array.from(by.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([m, pnl]) => {
      const d = new Date(`${m}-01T00:00:00Z`);
      const label = Number.isNaN(d.getTime())
        ? m
        : d.toLocaleDateString(undefined, { month: "short", year: "2-digit", timeZone: "UTC" });
      return { date: label, pnl } as StrategyRdPeriodPnlBucket;
    });
  }, [daily]);
  const series = period === "daily" ? daily : period === "weekly" ? weekly : monthly;
  const data = useMemo(
    () =>
      series.map((b, i) => ({
        i,
        label: b.date || b.week_start || String(i + 1),
        pnl: b.pnl,
      })),
    [series]
  );
  if (daily.length === 0 && weekly.length === 0) return null;
  return (
    <div
      data-statistics-shadcn-card=""
      className={strategyRdSectionCardClassName}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={strategyRdCardTitleClassName}>PnL</h3>
        {daily.length > 0 ? (
          <Tabs data-trade-log-tabs="" value={period} onValueChange={(v) => setPeriod(v as "daily" | "weekly" | "monthly")}>
            <TabsList className="min-w-max">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              {weekly.length > 0 ? <TabsTrigger value="weekly">Weekly</TabsTrigger> : null}
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : null}
      </div>
      {data.length === 0 ? (
        <p className={strategyRdMetaClassName}>No closed PnL buckets for this period.</p>
      ) : (
        <div className={statisticsShadcnBarChartAreaClassName}>
          <ChartContainer config={pnlChartConfig} className={statisticsShadcnBarChartContainerClassName}>
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} hide={data.length > 24 && period !== "monthly"} />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={64}
                tickFormatter={(v) => formatStrategyRdMoney(Number(v), currency) ?? String(v)}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload as { label?: string } | undefined;
                      return row?.label || "PnL";
                    }}
                    formatter={(value) => formatStrategyRdMoney(Number(value), currency) ?? "N/A"}
                  />
                }
              />
              <Bar dataKey="pnl" isAnimationActive={false} radius={[3, 3, 3, 3]}>
                {data.map((row) => (
                  <Cell
                    key={row.i}
                    fill={row.pnl >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      )}
    </div>
  );
}

export type StrategyRdSimChartsProps = {
  overview: StrategyRdApplyOverview | null;
  /** Account currency (USD unless the quote couldn't be converted). */
  currency?: string | null;
};

export function StrategyRdSimCharts({
  overview,
  currency = null,
}: StrategyRdSimChartsProps): JSX.Element | null {
  const daily = overview?.daily_pnl ?? [];
  const weekly = overview?.weekly_pnl ?? [];
  if (daily.length === 0 && weekly.length === 0) return null;
  return (
    <div className="flex flex-col gap-4" data-strategy-rd-sim-charts="">
      {/* The equity path lives in "Account over the backtest" above (with its drawdown). */}
      <PeriodPnlChart daily={daily} weekly={weekly} currency={currency} />
    </div>
  );
}
